import { useState } from "react";
import { MapPin } from "lucide-react";
import { TV_BLUE } from "@/components/broadcast/TvFrame";
import { YtCamPlayer } from "@/components/broadcast/YtCamPlayer";
import { api } from "@/lib/api";
import { GA_PATH, projectCity, iconFor, BrightStage, Pill, _isCamDead, _markCamDead } from "@/components/broadcast/shared";

function CameraMapSlide({ cameras = [] }) {
  const cams = cameras.filter((c) => c.lat != null && c.lon != null).slice(0, 10);
  if (!cams.length) return null;
  // Map: GA_PATH is 400x460 → scale 2 and center it in the 1920x1080 frame
  const S = 2.05, MX = 960 - 200 * S, MY = 540 - 230 * S + 30;
  const pin = (c) => { const p = projectCity(c.lat, c.lon); return { x: MX + p.x * S, y: MY + p.y * S }; };
  const sorted = [...cams].sort((a, b) => b.lat - a.lat);
  const left = sorted.filter((_, i) => i % 2 === 0), right = sorted.filter((_, i) => i % 2 === 1);
  const CW = 330, CH = 150, GAP = 14, TOP = 150;
  const col = (list, x) => list.map((c, i) => ({ cam: c, x, y: TOP + i * (CH + GAP) }));
  const cards = [...col(left, 40), ...col(right, 1920 - 40 - CW)];
  return (
    <div className="relative h-full w-full overflow-hidden atmo-slide" data-testid="camera-map-slide">
      <svg viewBox="0 0 1920 1080" className="h-full w-full" preserveAspectRatio="xMidYMid slice">
        <defs>
          <filter id="cm-shadow" x="-10%" y="-10%" width="120%" height="130%"><feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#000" floodOpacity="0.55" /></filter>
        </defs>
        <g transform={`translate(${MX} ${MY}) scale(${S})`}>
          <path d={GA_PATH} fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" />
        </g>
        {cards.map(({ cam, x, y }) => {
          const p = pin(cam);
          const isLeft = x < 960;
          const ax = isLeft ? x + CW : x;
          const ay = y + CH / 2;
          const thumb = `https://i.ytimg.com/vi/${cam.youtube_id}/mqdefault.jpg`;
          return (
            <g key={cam.id} data-testid={`camera-map-card-${cam.id}`}>
              <path d={`M ${ax} ${ay} C ${(ax + p.x) / 2} ${ay}, ${(ax + p.x) / 2} ${p.y}, ${p.x} ${p.y}`} fill="none" stroke="#d90429" strokeWidth="2.5" strokeOpacity="0.85" />
              <circle cx={p.x} cy={p.y} r="11" fill="#d90429" stroke="#fff" strokeWidth="3" />
              <g filter="url(#cm-shadow)">
                <rect x={x} y={y} width={CW} height={CH} rx="8" fill="#0f1523" stroke="rgba(255,255,255,0.18)" />
                <image href={thumb} x={x + 6} y={y + 6} width={CW - 12} height={CH - 46} preserveAspectRatio="xMidYMid slice" />
                <rect x={x + 6} y={y + CH - 38} width={CW - 12} height={32} rx="4" fill="rgba(0,0,0,0.55)" />
                <circle cx={x + 20} cy={y + CH - 22} r="4" fill="#d90429" />
                <text x={x + 30} y={y + CH - 16} fill="#fff" fontFamily="Oswald" fontWeight="700" fontSize="15" letterSpacing="1.5">{((n) => (n.length > 19 ? n.slice(0, 18) + "…" : n))((cam.name || "").toUpperCase())}</text>
                {cam.weather?.temp != null && (
                  <g>
                    <rect x={x + CW - 92} y={y + CH - 38} width={86} height={32} rx="4" fill="#d90429" />
                    <text x={x + CW - 49} y={y + CH - 14} textAnchor="middle" fill="#fff" fontFamily="Oswald" fontWeight="700" fontSize="24">{`${cam.weather.temp}°`}</text>
                  </g>
                )}
              </g>
            </g>
          );
        })}
        <text x="960" y="72" textAnchor="middle" fill="#d90429" fontFamily="Oswald" fontSize="18" letterSpacing="7">LIVE CAMERA NETWORK</text>
        <text x="960" y="122" textAnchor="middle" fill="#fff" fontFamily="Oswald" fontWeight="700" fontSize="52" letterSpacing="3">GEORGIA CAMERA MAP</text>
        <text x="960" y="1000" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontFamily="IBM Plex Mono" fontSize="14" letterSpacing="3">{`${cams.length} LIVE VIEWS · TEMPS FROM NEAREST NWS STATION`}</text>
      </svg>
    </div>
  );
}
let _camWallVisit = 0; // rotates the 4-up page each time the wall comes around in the loop
function LiveCameraWallSlide({ cameras: allCams = [] }) {
  const [visit] = useState(() => _camWallVisit++);
  const [, bump] = useState(0);
  const live = allCams.filter((c) => !_isCamDead(c.id));
  const cameras = live.length ? live : allCams; // never blank the wall — fall back to all cams (Connecting cover shows)
  const markDead = (cam, code) => {
    if (_isCamDead(cam.id)) return;
    _markCamDead(cam.id);
    api.post(`/cameras/${cam.id}/embed-error`, { code }).catch(() => {});
    bump((n) => n + 1);
  };
  if (!cameras.length) {
    return (
      <BrightStage testid="camera-wall-slide" className="flex items-center justify-center">
        <div className="text-center" data-testid="camera-wall-empty">
          <Pill color="#d90429">Live cameras · Georgia</Pill>
          <div className="mt-6 font-oswald text-[64px] font-black uppercase leading-none text-[#0a0f1c]">Camera feeds are refreshing</div>
          <div className="mt-3 font-oswald text-[24px] uppercase tracking-[0.3em] text-[#5b6675]">Live views return automatically when YouTube restores them</div>
        </div>
      </BrightStage>
    );
  }

  const pages = Math.max(1, Math.ceil(cameras.length / 4));
  const page = visit % pages;
  const feeds = cameras.slice(page * 4, page * 4 + 4);
  const gridCols = feeds.length >= 2 ? "grid-cols-2" : "grid-cols-1";
  const gridRows = feeds.length >= 3 ? "grid-rows-2" : "grid-rows-1";
  const heroFirst = feeds.length === 3; // 3 feeds: first one spans both rows so no quadrant sits empty

  return (
    <BrightStage testid="camera-wall-slide" className="p-[48px]">
      <div className="mb-5 flex items-center justify-between">
        <Pill color="#d90429"><span className="live-dot" style={{ width: 10, height: 10 }} /> Live cameras · Georgia</Pill>
        <div className="font-oswald text-[20px] font-bold uppercase tracking-[0.3em] text-[#5b6675]" data-testid="camera-wall-page">
          {pages > 1 ? `Cameras ${page * 4 + 1}–${page * 4 + feeds.length} of ${cameras.length}` : `${cameras.length} live feeds`}
        </div>
      </div>
      <div className={`grid h-[calc(100%-80px)] gap-5 ${gridCols} ${gridRows}`}>
        {feeds.map((cam, i) => {
          const crop = Math.max(0, Math.min(Number(cam.crop_bottom_pct || 0), 30));
          const scale = crop > 0 ? 1 / (1 - crop / 100) : 1;
          const wx = cam.weather;
          return (
            <div key={cam.id} className={`flex flex-col overflow-hidden rounded-[6px] bg-white shadow-[0_12px_30px_rgba(10,15,28,0.15)] ${heroFirst && i === 0 ? "row-span-2" : ""}`} data-testid={`cam-tile-${cam.id}`}>
              <div className="relative flex-1 overflow-hidden bg-black">
                <YtCamPlayer
                  videoId={cam.youtube_id}
                  title={cam.name}
                  style={crop > 0 ? { transform: `scale(${scale.toFixed(3)})`, transformOrigin: "center top" } : undefined}
                  onError={(code) => markDead(cam, code)}
                />
                <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-2 rounded-[3px] bg-[#d90429] px-3 py-1 font-oswald text-[18px] font-black uppercase tracking-[0.25em] text-white">
                  <span className="live-dot" style={{ width: 8, height: 8 }} /> Live
                </div>
                {wx && wx.temp != null && (
                  <div className="pointer-events-none absolute right-4 top-4 flex items-center gap-3 rounded-[4px] px-4 py-2 text-white shadow-[0_8px_24px_rgba(0,0,0,0.35)]" style={{ background: TV_BLUE }} data-testid={`cam-weather-${cam.id}`}>
                    {iconFor(wx.condition, "h-9 w-9")}
                    <span className="font-oswald text-[44px] font-black leading-none">{wx.temp}°</span>
                    <span className="max-w-[160px] truncate font-oswald text-[16px] font-bold uppercase leading-tight tracking-wider text-white/85">{wx.condition}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between gap-4 px-5 py-3 pr-[200px]">
                <div className="min-w-0">
                  <div className="truncate font-oswald text-[30px] font-black uppercase leading-none tracking-wide text-[#0a0f1c]">{cam.name}</div>
                  {cam.location && <div className="mt-1 flex items-center gap-1.5 truncate font-oswald text-[18px] font-bold uppercase tracking-[0.2em] text-[#5b6675]"><MapPin className="h-4 w-4 shrink-0" /> {cam.location}</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </BrightStage>
  );
}
let _featCamVisit = 0; // rotates the featured full-screen camera each loop pass
function FullScreenCamSlide({ cameras: allCams = [] }) {
  const [, bump] = useState(0);
  const [visit] = useState(() => _featCamVisit++);
  const live = allCams.filter((c) => !_isCamDead(c.id));
  const cameras = live.length ? live : allCams; // never render an empty frame
  if (!cameras.length) return null;
  const cam = cameras[visit % cameras.length];
  const crop = Math.max(0, Math.min(Number(cam.crop_bottom_pct || 0), 30));
  const scale = crop > 0 ? 1 / (1 - crop / 100) : 1;
  const wx = cam.weather;
  const markDead = (c, code) => {
    if (_isCamDead(c.id)) return;
    _markCamDead(c.id);
    api.post(`/cameras/${c.id}/embed-error`, { code }).catch(() => {});
    bump((n) => n + 1);
  };
  return (
    <div className="relative h-full w-full overflow-hidden bg-black" data-testid="camera-featured-slide">
      <YtCamPlayer
        videoId={cam.youtube_id}
        title={cam.name}
        style={crop > 0 ? { transform: `scale(${scale.toFixed(3)})`, transformOrigin: "center top" } : undefined}
        onError={(code) => markDead(cam, code)}
      />
      <div className="pointer-events-none absolute right-6 top-6 flex items-center gap-2 rounded-[3px] bg-[#d90429] px-4 py-1.5 font-oswald text-[20px] font-black uppercase tracking-[0.3em] text-white">
        <span className="live-dot" style={{ width: 9, height: 9 }} /> Live
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0">
        <div className="h-[4px] w-full" style={{ background: TV_BLUE }} />
        <div className="flex items-end justify-between gap-6 bg-gradient-to-t from-black/90 via-black/65 to-transparent px-10 pb-9 pt-24">
          <div className="min-w-0">
            <div className="mb-1 font-oswald text-[16px] font-bold uppercase tracking-[0.35em] text-[#ffd60a]">Georgia Weather Network · Live Cam</div>
            <div className="truncate font-oswald text-[64px] font-black uppercase leading-[0.9] text-white" data-testid="camera-featured-name">{cam.name}</div>
            {cam.location && (
              <div className="mt-1.5 flex items-center gap-2 font-oswald text-[26px] font-bold uppercase tracking-[0.2em] text-white/70">
                <MapPin className="h-6 w-6 shrink-0" /> {cam.location}
              </div>
            )}
          </div>
          {wx && wx.temp != null && (
            <div className="flex shrink-0 items-center gap-4 rounded-[6px] px-6 py-4 text-white shadow-[0_10px_30px_rgba(0,0,0,0.5)]" style={{ background: TV_BLUE }} data-testid="camera-featured-weather">
              {iconFor(wx.condition, "h-14 w-14")}
              <div className="leading-none">
                <div className="font-oswald text-[72px] font-black leading-none">{wx.temp}°</div>
                <div className="mt-1 max-w-[220px] truncate font-oswald text-[20px] font-bold uppercase tracking-wider text-white/85">{wx.condition}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { CameraMapSlide, LiveCameraWallSlide, FullScreenCamSlide };
